import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import { Info } from "lucide-react";

import { UIActionModal, UICallout } from "../../../ui";
import messages from "../../../../assets/locale/messages";

const UnassignSupervisorModal = ({
  open,
  supervisorName,
  supervisorImage,
  supervisorUserId,
  zoneName,
  onClose,
  onConfirm,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { zoneApplicants, shared } = messages[lang];
  const t = zoneApplicants.unassignSupervisorModal;

  // Same shape as the applicant summary line: id · zone · role. The zone is the
  // only part that can run long, so it carries the ellipsis.
  const summaryParts = [
    supervisorUserId ? `#${supervisorUserId}` : null,
    zoneName ? (
      <span
        key="zone"
        className="zone-applicants__unassign-zone"
        title={zoneName}
      >
        {zoneName}
      </span>
    ) : null,
    zoneApplicants.supervisor,
  ].filter(Boolean);

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      variant="reject"
      title={t.title}
      className="zone-applicants__unassign-modal"
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={zoneApplicants.unassign}
          confirmVariant="reject"
          onCancel={onClose}
          onConfirm={onConfirm}
        />
      }
    >
      <Typography className="zone-applicants__unassign-question">
        {t.confirmationMsg}
      </Typography>

      <UIActionModal.UserInfo
        name={supervisorName || "-"}
        image={supervisorImage}
        description={summaryParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 ? " · " : null}
            {part}
          </React.Fragment>
        ))}
      />

      <UICallout
        variant="error"
        icon={<Info size={20} strokeWidth={2} className="ui-callout__icon" />}
        title={t.warningTitle}
        description={t.warningDescription}
        hasBorder={false}
      />
    </UIActionModal>
  );
};

UnassignSupervisorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  supervisorName: PropTypes.string,
  supervisorImage: PropTypes.string,
  /** User id of the supervisor, rendered as `#id` in the summary line. */
  supervisorUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  zoneName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

UnassignSupervisorModal.defaultProps = {
  supervisorName: "",
  supervisorImage: undefined,
  supervisorUserId: undefined,
  zoneName: "",
};

export default UnassignSupervisorModal;
